/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { gql, useQuery } from '@apollo/client';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import { css, jsx } from '@emotion/react';
import { DocumentNode, parse } from 'graphql';
import moment from 'moment';
import * as monacoEditor from 'monaco-editor';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { Loading } from 'src/components/common/loading/Loading';
import { NoData } from 'src/components/common/noData/NoData';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import {
  StudioDiffEditorContainer,
  useModelsAndDiffEditorOutOfSync,
  useStudioDiffEditor,
} from 'src/hooks/useStudioDiffEditor';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';
import {
  setOverlayClosedLocation,
  setOverlayVisibleLocation,
} from 'src/lib/routing';

import { useGraphRef } from '../../hooks/useGraphRef';
import { launchesRouteConfig } from '../../routes';
import { DiffNavigatorButtons } from '../../shared/diffEditor/DiffNavigatorButtons';
import { HiddenSectionIndicators } from '../../shared/diffEditor/HiddenSectionIndicators';
import { useHideUnchangedLines } from '../../shared/diffEditor/useHideUnchangedLines';
import { WrapDiffLinesDropdown } from '../../shared/diffEditor/WrapDiffLinesDropdown';

import { LaunchSelect } from './LaunchSelect';

export const LaunchSupergraphDiffViewerQuery = gql<
  GraphQLTypes.LaunchSupergraphDiffViewerQuery,
  GraphQLTypes.LaunchSupergraphDiffViewerQueryVariables
>`
  query LaunchSupergraphDiffViewerQuery(
    $graphId: ID!
    $graphVariant: String!
    $launchId: ID!
  ) {
    service(id: $graphId) {
      id
      variant(name: $graphVariant) {
        id
        launch(id: $launchId) {
          id
          build {
            id
            result {
              ... on BuildSuccess {
                coreSchema {
                  coreHash
                  coreDocument
                }
              }
            }
          }
        }
      }
    }
  }
`;

interface Launch {
  id: string;
  shortenedID: string;
  createdAt: moment.MomentInput;
}

export const LaunchSupergraphDiffViewer = ({
  isOpen,
  launches,
  baseLaunchId,
  baseLaunchSchema,
}: {
  isOpen: boolean;
  // Some list assumptions:
  // - Contains the base launch.
  // - At least length 2.
  // - Launch IDs are unique.
  // - Sorted by createdAt descending.
  launches: Launch[];
  baseLaunchId: string;
  baseLaunchSchema: string;
}) => {
  // When computing "x ago" text in the launch select, we'd like those times
  // to not frequently change whenever render decides to run, as it may be
  // distracting to look at, and may give the impression the times update with
  // a regular cadence when they really don't (which may appear as jumpiness
  // for smaller durations). So we let "now" be the modal's mount time.
  const [modalMountTimestamp] = useState(new Date());

  // Note the route is the source of truth for comparison ID.
  let { comparisonId: routeComparisonLaunchId } =
    useRouteParams(launchesRouteConfig);

  // Compute map of launches by ID, and determine the default comparison launch
  // (the preceding launch if it exists, otherwise the subsequent launch).
  const launchesById: { [key: string]: Launch } = Object.create(null);
  let defaultComparisonLaunchId: string | null = null;
  let prevLaunchId: string | null = null;
  let prevPrevLaunchId: string | null = null;
  launches.forEach((launch) => {
    if (baseLaunchId === prevLaunchId) {
      defaultComparisonLaunchId = launch.id;
    }
    launchesById[launch.id] = launch;
    prevPrevLaunchId = prevLaunchId;
    prevLaunchId = launch.id;
  });
  if (baseLaunchId === prevLaunchId) {
    defaultComparisonLaunchId = prevPrevLaunchId;
  }
  if (defaultComparisonLaunchId === null) {
    throw new Error('Could not determine default comparison launch');
  }

  // If we don't know about the comparison ID or it's the same as base, assume
  // default.
  if (
    routeComparisonLaunchId !== undefined &&
    (!launchesById[routeComparisonLaunchId] ||
      routeComparisonLaunchId === baseLaunchId)
  ) {
    routeComparisonLaunchId = undefined;
  }

  const history = useHistory();
  const location = useLocation();
  const setComparisonLaunchId = (comparisonLaunchId: string) => {
    // Avoid changing route state unless needed.
    if (comparisonLaunchId !== routeComparisonLaunchId) {
      history.push(
        setOverlayVisibleLocation({
          location: launchesRouteConfig.locationFrom({
            location,
            fromRouteConfig: launchesRouteConfig,
            patch: {
              [Config.queryParameters.ComparisonId]: comparisonLaunchId,
            },
          }),

          overlayName: Config.modals.supergraphDiffViewer,
        }),
      );
    }
  };

  const comparisonLaunchId =
    routeComparisonLaunchId ?? defaultComparisonLaunchId;
  useEffect(() => {
    // Set the route comparison ID if different.
    setComparisonLaunchId(comparisonLaunchId);
  });

  const baseLaunch = launchesById[baseLaunchId];
  const comparisonLaunch = launchesById[comparisonLaunchId];
  if (!baseLaunch || !comparisonLaunch) {
    throw new Error('Could not fetch base launch or comparison launch');
  }

  const onClose = () => {
    // Note that we explicitly do not call closeModal() here. This is because it
    // unsets the overlay parameter without also unsetting comparisonId. To
    // avoid that, we manually unset both the overlay and comparisonId
    // parameters here.
    //
    // Note that you also can't call closeModal() after this, because it will
    // set comparisonId (because it uses an old location for calculating what
    // the new location should be).
    history.push(
      setOverlayClosedLocation(
        launchesRouteConfig.locationFrom({
          location,
          fromRouteConfig: launchesRouteConfig,
          patch: {
            [Config.queryParameters.ComparisonId]: RouteConfig.DELETE,
          },
        }),
      ),
    );
  };

  const graphRef = useGraphRef();
  if (!graphRef) {
    throw new Error('Could not find a graph ref');
  }

  const { data, loading, error } = useQuery(LaunchSupergraphDiffViewerQuery, {
    variables: {
      graphId: graphRef.graphId,
      graphVariant: graphRef.graphVariant,
      launchId: comparisonLaunchId,
    },
  });

  if (error) {
    throw error;
  }

  const build = data?.service?.variant?.launch?.build?.result;
  const comparisonLaunchSchema =
    build?.__typename === 'BuildSuccess'
      ? build.coreSchema.coreDocument
      : undefined;

  const [shouldWrapLines, setShouldWrapLines] = useLocalStorage(
    'sandboxDiffShouldWrapLines',
  );
  const sharedMonacoOptions:
    | monacoEditor.editor.IStandaloneEditorConstructionOptions
    | monacoEditor.editor.IDiffEditorConstructionOptions = useMemo(
    () => ({ wordWrap: shouldWrapLines ? 'on' : 'off' }),
    [shouldWrapLines],
  );

  const { diffEditor, diffNavigator } = useStudioDiffEditor({
    monacoOptions: sharedMonacoOptions,
    language: 'graphql',
    originalFile: comparisonLaunchSchema ?? undefined,
    modifiedFile: baseLaunchSchema,
  });
  const { modifiedHiddenRanges, originalHiddenRanges, handleExpandRange } =
    useHideUnchangedLines({
      diffEditor,
    });
  const [typeDefs, setTypeDefs] = useState<{
    modified: DocumentNode | undefined;
    original: DocumentNode | undefined;
  }>();
  useEffect(() => {
    diffEditor.onDidUpdateDiff(() => {
      const modifiedSdl = diffEditor.getModifiedEditor().getModel()?.getValue();
      const originalSdl = diffEditor.getOriginalEditor().getModel()?.getValue();
      setTypeDefs({
        modified: modifiedSdl ? parse(modifiedSdl) : undefined,
        original: originalSdl ? parse(originalSdl) : undefined,
      });
    });
  }, [diffEditor]);

  const modelsAndDiffOutOfSync = useModelsAndDiffEditorOutOfSync({
    diffEditor,
  });

  const originalTypeDefs = typeDefs?.original;
  const modifiedTypeDefs = typeDefs?.modified;
  const shouldShowCollapsedSections =
    originalTypeDefs &&
    modifiedTypeDefs &&
    originalHiddenRanges &&
    modifiedHiddenRanges;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore expression too complex iwth jsx pragma */}
      <ModalOverlay />
      <ModalContent className="w-[85%]">
        <ModalHeader>
          <ModalTitle>Supergraph diff</ModalTitle>
          <div className="flex justify-between">
            <div className="flex items-center">
              <LaunchSelect
                name="Compared from"
                launches={launches.filter(
                  (launch) => launch.id !== baseLaunchId,
                )}
                currentTimestamp={modalMountTimestamp}
                launchId={comparisonLaunchId}
                onChange={(launchId) => setComparisonLaunchId(launchId)}
              />
              <div className="pr-4" />
              <LaunchSelect
                name="Base"
                disabled
                launches={[baseLaunch]}
                currentTimestamp={modalMountTimestamp}
                launchId={baseLaunchId}
              />
            </div>
            {baseLaunchSchema && comparisonLaunchSchema && (
              <div className="flex items-center">
                <DiffNavigatorButtons
                  diffEditor={diffEditor}
                  diffNavigator={diffNavigator}
                  disabled={loading}
                />
                <WrapDiffLinesDropdown
                  shouldWrapLines={shouldWrapLines}
                  setShouldWrapLines={setShouldWrapLines}
                />
              </div>
            )}
          </div>
        </ModalHeader>
        <ModalBody className="px-0">
          {
            // Monaco has its own scroll bar, and we don't want to show two scroll
            // bars to the user because it's really annoying. To that end, we take
            // the Modal size (80vh) and subtract the height of the headers and
            // footers (plus some extra), and make that the height. Ideally we could
            // compute this dynamically, but that takes some effort.
          }
          <div style={{ height: 'calc(80vh - 300px)' }}>
            {loading || modelsAndDiffOutOfSync ? (
              <Loading />
            ) : comparisonLaunchSchema ? (
              <>
                {shouldShowCollapsedSections && (
                  <>
                    <HiddenSectionIndicators
                      editor={diffEditor.getModifiedEditor()}
                      typeDefs={modifiedTypeDefs as DocumentNode}
                      rangesToHide={
                        modifiedHiddenRanges as monacoEditor.Range[]
                      }
                      handleExpandRange={handleExpandRange}
                    />
                    <HiddenSectionIndicators
                      editor={diffEditor.getOriginalEditor()}
                      typeDefs={originalTypeDefs as DocumentNode}
                      rangesToHide={
                        originalHiddenRanges as monacoEditor.Range[]
                      }
                      handleExpandRange={handleExpandRange}
                    />
                  </>
                )}
                <div
                  className="size-full"
                  css={css`
                    .xxx-monaco-expanded-lines {
                      background-color: rgb(244, 246, 248);
                      z-index: -1;
                    }
                  `}
                >
                  <StudioDiffEditorContainer
                    diffEditor={diffEditor}
                    editorHeight="100%"
                    className="size-full"
                  />
                </div>
              </>
            ) : (
              <NoData
                message={`Could not find the schema ${comparisonLaunch.shortenedID}`}
              />
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
